import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Ship, Inbox, FileText, Search, Megaphone, Settings as SettingsIcon, LogOut, Anchor, ExternalLink, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { name: "Dashboard", path: "/admin", icon: LayoutDashboard, end: true },
  { name: "Inventory", path: "/admin/inventory", icon: Ship },
  { name: "Brands / Models", path: "/admin/models", icon: Database },
  { name: "Leads", path: "/admin/leads", icon: Inbox },
  { name: "Site Content", path: "/admin/site", icon: FileText },
  { name: "SEO", path: "/admin/seo", icon: Search },
  { name: "Promotions", path: "/admin/promotions", icon: Megaphone },
  { name: "Settings", path: "/admin/settings", icon: SettingsIcon },
];

export function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-layout">
      <aside className="w-60 bg-mtm-charcoal text-slate-300 flex flex-col fixed inset-y-0 left-0 z-40">
        <Link to="/admin" className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800">
          <span className="h-9 w-9 rounded-lg bg-gradient-to-br from-mtm-violet to-mtm-teal flex items-center justify-center text-white"><Anchor size={20} /></span>
          <div className="flex flex-col leading-none"><span className="font-heading font-bold text-white">MTM Admin</span><span className="text-[9px] font-mono uppercase tracking-widest text-mtm-teal-bright">Console</span></div>
        </Link>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.name} to={n.path} end={n.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-mtm-purple text-white" : "hover:bg-slate-800 hover:text-white"}`}
              data-testid={`admin-nav-${n.name.toLowerCase().replace(/[\s/]+/g, "-")}`}>
              <n.icon size={18} /> {n.name}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-slate-800 hover:text-white"><ExternalLink size={18} /> View Site</a>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-slate-800 hover:text-white" data-testid="admin-logout-button"><LogOut size={18} /> Sign Out</button>
        </div>
      </aside>
      <div className="flex-1 ml-60">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export function AdminHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
